import { useState } from "react";
import { ShirtIcon, X, MessageCircle, SearchIcon, CalendarCheck, Megaphone } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const WHATSAPP_LOST_URL =
  "https://wa.me/491742022890?text=Hallo%2C%20ich%20habe%20etwas%20im%20Luther%20Club%20verloren.%20Beschreibung%3A%20";

const WHATSAPP_RESERVATION_URL =
  "https://wa.me/491742022890?text=Hallo%2C%20ich%20m%C3%B6chte%20gerne%20einen%20Tisch%20reservieren.%0AName%3A%20%0ADatum%3A%20%0AUhrzeit%3A%20%0AAnzahl%20Personen%3A%20";

interface LostAndFoundProps {
  showNewsButton?: boolean;
  onNewsClick?: () => void;
}

const LostAndFound = ({ showNewsButton, onNewsClick }: LostAndFoundProps) => {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  return (
    <>
      {/* Overlay backdrop on mobile */}
      {open && isMobile && (
        <div
          className="fixed inset-0 z-[998] bg-black/40"
          onClick={() => setOpen(false)}
        />
      )}

      <div className={`fixed z-[999] transition-all duration-300 ${isMobile ? "bottom-20 right-4" : "right-0 top-1/2 -translate-y-1/2"}`}>
        {/* Info panel */}
        <div
          className={`absolute transition-all duration-300 ease-in-out ${
            open
              ? "opacity-100 scale-100 pointer-events-auto"
              : "opacity-0 scale-95 pointer-events-none"
          } ${
            isMobile
              ? "bottom-14 right-0 w-72"
              : "right-full top-1/2 -translate-y-1/2 mr-3 w-80"
          }`}
        >
          <div className="rounded-lg border border-border/50 bg-card p-5 shadow-xl">
            <div className="flex items-start justify-between mb-3">
              <h3
                className="text-base font-semibold tracking-wider uppercase text-primary"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Etwas verloren?
              </h3>
              <button
                onClick={() => setOpen(false)}
                className="text-muted-foreground hover:text-foreground transition-colors -mt-1 -mr-1 p-1"
                aria-label="Schließen"
              >
                <X size={16} />
              </button>
            </div>

            <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
              Schreib uns per WhatsApp mit einer kurzen Beschreibung deines
              Gegenstands – wir prüfen, ob er bei uns abgegeben wurde.
            </p>

            <div className="text-xs text-muted-foreground mb-4 border-t border-border/30 pt-3">
              <p className="font-medium text-foreground/80 mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Abholung:
              </p>
              <p>Donnerstags 10 – 14 Uhr</p>
              <p>oder am nächsten Wochenende</p>
            </div>

            <a
              href={WHATSAPP_LOST_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full rounded-md bg-[#25D366] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#20bd5a] transition-colors"
            >
              <MessageCircle size={16} />
              Per WhatsApp melden
            </a>
          </div>
        </div>

        {/* Buttons stack */}
        <div className={`flex ${isMobile ? "flex-col gap-3" : "flex-col gap-1"}`}>
          {/* Reservation button */}
          {isMobile ? (
            <a
              href={WHATSAPP_RESERVATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-12 h-12 rounded-full bg-primary border border-primary/50 text-primary-foreground shadow-lg hover:bg-primary/80 transition-colors"
              aria-label="Reservierung"
            >
              <CalendarCheck size={20} />
            </a>
          ) : (
            <a
              href={WHATSAPP_RESERVATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 bg-primary border border-primary/50 border-r-0 rounded-l-md px-2 py-3 text-primary-foreground shadow-lg hover:bg-primary/80 transition-colors"
              style={{ writingMode: "vertical-rl" }}
              aria-label="Reservierung"
            >
              <CalendarCheck size={14} className="rotate-90" />
              <span
                className="text-xs font-medium tracking-widest uppercase"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Reservierung
              </span>
            </a>
          )}

          {/* Fundsachen button */}
          {isMobile ? (
            <button
              onClick={() => setOpen(!open)}
              className="relative flex items-center justify-center w-12 h-12 rounded-full bg-secondary border border-border/50 text-primary shadow-lg hover:bg-secondary/80 transition-colors"
              aria-label="Fundsachen"
            >
              <ShirtIcon size={20} />
              <SearchIcon size={13} className="absolute bottom-1.5 right-1.5 text-primary/80" />
            </button>
          ) : (
            <button
              onClick={() => setOpen(!open)}
              className="relative flex items-center gap-1.5 bg-secondary border border-border/50 border-r-0 rounded-l-md px-2 py-3 text-primary shadow-lg hover:bg-secondary/80 transition-colors"
              style={{ writingMode: "vertical-rl" }}
              aria-label="Fundsachen"
            >
              <span className="relative inline-block">
                <ShirtIcon size={14} className="rotate-90" />
                <SearchIcon size={10} className="absolute -bottom-1 -right-1 text-primary/80" />
              </span>
              <span
                className="text-xs font-medium tracking-widest uppercase"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Fundsachen
              </span>
            </button>
          )}

          {/* News button */}
          {showNewsButton && (
            isMobile ? (
              <button
                onClick={onNewsClick}
                className="flex items-center justify-center w-12 h-12 rounded-full bg-secondary border border-border/50 text-primary shadow-lg hover:bg-secondary/80 transition-colors"
                aria-label="Ankündigung öffnen"
              >
                <Megaphone size={20} />
              </button>
            ) : (
              <button
                onClick={onNewsClick}
                className="flex items-center gap-1.5 bg-secondary border border-border/50 border-r-0 rounded-l-md px-2 py-3 text-primary shadow-lg hover:bg-secondary/80 transition-colors"
                style={{ writingMode: "vertical-rl" }}
                aria-label="Ankündigung öffnen"
              >
                <Megaphone size={14} className="rotate-90" />
                <span
                  className="text-xs font-medium tracking-widest uppercase"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  News
                </span>
              </button>
            )
          )}
        </div>
      </div>
    </>
  );
};

export default LostAndFound;
